import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { message } from 'antd';
import { useParams } from 'react-router-dom';

import AnalyticsEventsAPI from '../../../../api/AnalyticsEventsAPI';
import CommunityAPI from '../../../../api/CommunityAPI';
import Header from '../../../../components/Header/Header';
import MenuButton from '../../../../components/MenuButton/MenuButton';
import {
  resetReplyCommentObj,
  setCommunityPostComments,
  setPDPCommunityPostData,
  setReplyToComment,
  updateCommunitPost,
} from '../../../../context/communityReducer';
import showAppError from '../../../../shared/error';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  IPollVoteByUser,
  PollOption,
} from '../../../../types/communityMessagesTypes';
import {
  IChannelPostDetails,
  IPostMediaItem,
} from '../../../../types/communityTypes';
import { PlatformIntegrationPlatformEnum } from '../../../../types/integrationType';
import { ROUTES } from '../../../../types/routes';
import CreatePostMediaRender from '../../../CreateCommunityPost/components/CreatePostMediaRender/CreatePostMediaRender';
import PollVotedUserListModal from '../../components/PollVotedUserListModal';
import CommunityPostCommentInput from './CommunityPostCommentInput';
import CommunityPostCommentsList from './CommunityPostCommentsList';
import CommunityPostContent from './CommunityPostContent';
import CommunityPostHeader from './CommunityPostHeader';
import CommunityPostItem from './CommunityPostItem';
import CommunityPostMenuModel from './CommunityPostMenuModel';
import CommunityPostPoll from './CommunityPostPoll';
import CommunityPostStats from './CommunityPostStats';

type Props = {};

interface IUserState {
  page: number;
  users: IPollVoteByUser[];
  loading: boolean;
  hasMore: boolean;
}

const CommunityPostDetails: React.FC<Props> = ({}) => {
  const intialState: IUserState = useMemo(
    () => ({
      page: 1,
      users: [],
      loading: false,
      hasMore: true,
    }),
    [],
  );
  const dispatch = useAppDispatch();
  const { postId, channelId } = useParams();
  const navigate = useAppNavigate();

  const { pdpCommunityPostData: communityPostData } = useAppSelector(
    (state) => state?.community,
  );

  const userDetails = useAppSelector((state) => state?.user);

  const [loadingPost, setLoadingPost] = useState(false);
  const [showMenuModel, setShowMenuModel] = useState(false);
  const [selectedOption, setSelectedOption] = useState('all');
  const [showPollVotedUserList, setShowPollVotedUserList] = useState(false);
  const [userState, setUserState] = useState(intialState);
  const [selectedTotalVotesCount, setSelectedTotalVotesCount] = useState(0);

  const fetchUserList = async () => {
    if (userState?.loading) return;
    if (!userState?.hasMore) return;
    try {
      setUserState((prev) => ({
        ...prev,
        loading: true,
      }));
      const query = new URLSearchParams();
      query.append('page', userState.page.toString());
      query.append('pageSize', '10');
      if (selectedOption !== 'all') {
        query.append('options', selectedOption);
      }
      const resp = await CommunityAPI.getPollVotesInPost(
        channelId as string,
        postId as string,
        query?.toString() || '',
      );
      if (resp.status === 200) {
        setUserState((prev) => ({
          ...prev,
          users: [...prev?.users, ...resp?.data?.result?.votes],
          loading: false,
          hasMore: resp?.data?.result?.votes?.length === 10,
          page: prev?.page + 1,
        }));
        setSelectedTotalVotesCount(resp?.data?.result?.totalVotes || 0);
      } else {
        showAppError(resp);
        setUserState((prev) => ({
          ...prev,
          loading: false,
          hasMore: false,
        }));
      }
    } catch (error) {
      showAppError(error);
      setUserState((prev) => ({
        ...prev,
        loading: false,
        hasMore: false,
      }));
    }
  };

  const increasePostViewCount = useCallback(async () => {
    if (!postId || !channelId) return;
    try {
      const resp = await CommunityAPI.increasePostViews(channelId, postId);
      if (resp.status === 201) {
        dispatch(
          setPDPCommunityPostData({
            ...communityPostData,
            totalViews: (communityPostData?.totalViews || 0) + 1,
          }),
        );
        try {
          AnalyticsEventsAPI.trackCommunityEvents('POST_VIEWED', {
            user_id: userDetails.id,
            onboarding: userDetails.type,
            name: userDetails.name,
            email: userDetails.email,
            phone_number: userDetails.phone,
            host: userDetails.host,
            platform: PlatformIntegrationPlatformEnum.MOBILE,
            post_creator_id: communityPostData?.postedBy,
            post_id: postId,
            timestamp: new Date().toISOString(),
          });
        } catch (error: any) {
          console.log('Error in tracking post viewed events:', error);
        }
      }
    } catch (error) {
      console.log('Error while increasing post views', error);
    }
  }, [
    channelId,
    communityPostData,
    dispatch,
    postId,
    userDetails.email,
    userDetails.host,
    userDetails.id,
    userDetails.name,
    userDetails.phone,
    userDetails.type,
  ]);

  const getPost = useCallback(
    async (cId: string, pId: string) => {
      try {
        const res = await CommunityAPI.getCommunityPostById(cId, pId);
        if (res.status === 200) {
          dispatch(setPDPCommunityPostData(res.data.result.post));
          setLoadingPost(false);
        } else {
          showAppError(res.data);
        }
      } catch (err: any) {
        showAppError(err);
        if (err?.response && err?.response.status === 403) {
          dispatch(setPDPCommunityPostData(err.response.data.result));
          setLoadingPost(false);
        }
      }
    },
    [dispatch],
  );

  const vote = async (optionId?: string) => {
    if (!communityPostData?._id) return;
    if (!optionId) {
      message.warning('Something want wrong, please refresh and try again');
      return;
    }
    if (communityPostData?.votedOption) {
      if (communityPostData?.post?.poll?.allowVoteChange) {
        message.info(
          'You alreay voted, but if you wish to change your vote click undo',
        );
        return;
      }
      message.info('You alreay voted');
      return;
    }
    try {
      const resp = await CommunityAPI.addVoteOnCommunityPoll(
        channelId as string,
        communityPostData?._id,
        { option: optionId },
      );
      if (resp?.status === 201) {
        const newVoteCountObj = communityPostData?.pollVotesPerOption;
        if (newVoteCountObj?.[optionId]) {
          newVoteCountObj[optionId] = (newVoteCountObj?.[optionId] || 0) + 1;
        }
        const newTotalVotes = (communityPostData?.totalPollVotes || 0) + 1;

        dispatch(
          updateCommunitPost({
            ...communityPostData,
            votedOption: optionId,
            pollVotesPerOption: newVoteCountObj,
            totalPollVotes: newTotalVotes,
          }),
        );
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const undoVote = async (optionId?: string) => {
    if (!communityPostData?._id) return;
    if (!optionId) {
      message.warning('Something want wrong, please refresh and try again');
      return;
    }
    try {
      const resp = await CommunityAPI.removeVoteOnCommunityPoll(
        channelId as string,
        communityPostData?._id,
        { option: optionId },
      );
      if (resp?.status === 204) {
        const newVoteCountObj = communityPostData?.pollVotesPerOption;
        if (newVoteCountObj?.[optionId]) {
          newVoteCountObj[optionId] = newVoteCountObj?.[optionId] - 1;
        }
        const newTotalVotes = (communityPostData?.totalPollVotes || 0) - 1;

        dispatch(
          updateCommunitPost({
            ...communityPostData,
            votedOption: false,
            pollVotesPerOption: newVoteCountObj,
            totalPollVotes: newTotalVotes,
          }),
        );
      } else {
        showAppError(resp?.data);
      }
    } catch (error) {
      showAppError(error);
    }
  };

  const handleBack = () => {
    navigate.goBack();
  };

  const post = useMemo(() => {
    return communityPostData?.post;
  }, [communityPostData]);

  const postDetails = useMemo(() => {
    const newData = { ...communityPostData };
    delete newData.post;
    return newData;
  }, [communityPostData]);

  useEffect(() => {
    if (
      communityPostData?._id &&
      communityPostData?.postedBy !== userDetails?.id
    ) {
      increasePostViewCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityPostData?._id, communityPostData?.postedBy, userDetails?.id]);

  useEffect(() => {
    if (postId && channelId) {
      getPost(channelId as string, postId as string);
    }
  }, [postId, channelId, getPost]);

  useEffect(() => {
    return () => {
      dispatch(setPDPCommunityPostData(null));
      dispatch(setCommunityPostComments([]));
      dispatch(setReplyToComment(null));
      dispatch(resetReplyCommentObj());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedOption) {
      setUserState(intialState);
    }
  }, [intialState, selectedOption]);

  if (
    (loadingPost && !communityPostData?._id) ||
    !(communityPostData && post && postDetails)
  ) {
    return <div>Loading....</div>;
  }

  return (
    <>
      <div className="pdp-wrapper">
        <div className="community-post-item_wrapper community-feed-post community-post-item">
          <Header
            title={`Post`}
            handleBack={handleBack}
            actionItems={[
              <MenuButton
                onClick={() => {
                  setShowMenuModel(true);
                }}
              />,
            ]}
          />
          <div className="scroll">
            <CommunityPostHeader
              communityPostDetails={communityPostData}
              isRepost={false}
              isPDP
            />
            {post.caption && !post?.poll ? (
              <CommunityPostContent
                caption={post.caption}
                maxLines={post.mediaContent?.length ? 2 : 5}
                onCaptionPressed={() => {}}
                key={post?._id}
              />
            ) : null}
            {post?.mediaContent?.length ? (
              <CreatePostMediaRender
                postedBy={post?.postedBy?._id}
                files={post?.mediaContent.map(
                  (file) =>
                    ({
                      ...file,
                      type: file?.contentType,
                      url: file?.contentUrl,
                      name: file?.fileName,
                      postedBy: post?.postedBy?._id,
                      postId: communityPostData?._id,
                    }) as IPostMediaItem,
                )}
                selectedLayout={post?.layoutType}
              />
            ) : null}
            {post?.poll ? (
              <CommunityPostPoll
                undoVote={undoVote}
                vote={vote}
                poll={post?.poll}
                postedBy={post?.postedBy}
                pollVotesPerOption={postDetails?.pollVotesPerOption || {}}
                totalPollVotes={postDetails?.totalPollVotes as number}
                votedOption={postDetails?.votedOption as string | false}
                setSelectedOption={setSelectedOption}
                setShowPollVotedUserList={setShowPollVotedUserList}
              />
            ) : null}
            {post?.postRef ? (
              <div className="creatorPostsWrapper__repost-card">
                <CommunityPostItem
                  key={post?.postRef?._id}
                  // index={0}
                  communityPostDetails={
                    { post: post?.postRef } as IChannelPostDetails
                  }
                  isRepost={Boolean(post?.postRef?._id)}
                />
              </div>
            ) : null}

            <CommunityPostStats
              postId={post?._id}
              isPDPPage
              communityPostDetails={communityPostData}
              channelId={channelId}
              onRepostPressed={() =>
                navigate(
                  ROUTES.COMMUNITY_CREATE_POST,
                  {
                    postRefId:
                      communityPostData?.post?.postRef?._id ||
                      communityPostData._id,
                    channelId: channelId as string,
                  }, // If reposted post is getting reposted, then postRefId and postRefDetails will be the of original post which is being reposted in the first place
                  {
                    state: {
                      postRefDetails:
                        communityPostData?.post?.postRef || communityPostData,
                    },
                  },
                )
              }
            />
            <CommunityPostCommentsList />
          </div>
        </div>
        {post?.postSettings?.disableComment ? (
          <div className="disable-comment_wrapper">
            <p className="disable-comment_wrapper_text">
              Comments are disabled for this post.
            </p>
          </div>
        ) : (
          <CommunityPostCommentInput />
        )}
      </div>

      <CommunityPostMenuModel
        showMenu={showMenuModel}
        closeModel={() => setShowMenuModel(false)}
        communityPostDetails={communityPostData as IChannelPostDetails}
        isPDP
        onTogglePinPost={() => {}}
      />
      <PollVotedUserListModal
        showModal={showPollVotedUserList}
        closeModal={() => {
          setShowPollVotedUserList(false);
        }}
        question={communityPostData?.post?.poll?.question as string}
        pollOptions={communityPostData?.post?.poll?.pollOptions as PollOption[]}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        userList={userState?.users}
        totalPollVotes={selectedTotalVotesCount}
        hasMore={userState?.hasMore}
        fetchUserList={fetchUserList}
      />
    </>
  );
};

export default CommunityPostDetails;
